export type SentimentKeywordMode = "GLOBAL" | "CUSTOM";
export type SentimentKeywordDisplayStatus = "DISABLED" | "GLOBAL" | "CUSTOM";

export interface SentimentKeywordConfig {
  sentimentKeywordMode: SentimentKeywordMode;
  positiveSentimentKeywords: string[];
  negativeSentimentKeywords: string[];
}

export interface SentimentKeywordDisplayState extends SentimentKeywordConfig {
  status: SentimentKeywordDisplayStatus;
  statusLabel: string;
  description: string;
}

export const normalizeSentimentKeywordText = (value: unknown) => String(value ?? "").trim().replace(/\s+/g, " ");

export const normalizeSentimentKeywordKey = (value: unknown) => normalizeSentimentKeywordText(value).toLocaleLowerCase("ko-KR");

export const normalizeSentimentKeywordList = (value: unknown): string[] => {
  const rawValues = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];
  const byKey = new Map<string, string>();
  rawValues.forEach((rawValue) => {
    const keyword = normalizeSentimentKeywordText(rawValue);
    if (!keyword) return;
    byKey.set(normalizeSentimentKeywordKey(keyword), keyword);
  });
  return Array.from(byKey.values());
};

export const normalizeSentimentKeywordMode = (value: unknown): SentimentKeywordMode =>
  String(value ?? "").trim().toUpperCase() === "CUSTOM" ? "CUSTOM" : "GLOBAL";

export const buildSentimentKeywordConfigFromMonitoring = (item: any): SentimentKeywordConfig => {
  const sentimentKeywordMode = normalizeSentimentKeywordMode(item?.sentimentKeywordMode);
  return {
    sentimentKeywordMode,
    positiveSentimentKeywords: [],
    negativeSentimentKeywords:
      sentimentKeywordMode === "CUSTOM" ? normalizeSentimentKeywordList(item?.negativeSentimentKeywords) : [],
  };
};

export const buildSentimentKeywordDisplayState = (item: any): SentimentKeywordDisplayState => {
  const config = buildSentimentKeywordConfigFromMonitoring(item);
  const sentimentEnabled = item?.sentimentEnabled !== false;
  if (!sentimentEnabled) {
    return {
      ...config,
      status: "DISABLED",
      statusLabel: "부정 판정 미사용",
      description: "부정 판정이 비활성화되어 현재 기사 판별에는 키워드를 적용하지 않습니다.",
    };
  }
  if (config.sentimentKeywordMode === "CUSTOM") {
    return {
      ...config,
      status: "CUSTOM",
      statusLabel: "모니터링별 부정 키워드",
      description: "이 모니터링에 저장된 맞춤 부정 키워드를 적용합니다.",
    };
  }
  return {
    ...config,
    status: "GLOBAL",
    statusLabel: "전역 키워드 사용",
    description: "전역 키워드 설정을 적용합니다. 이 화면에서는 전역 목록을 표시하지 않습니다.",
  };
};

export const upsertSentimentKeyword = (keywords: string[], value: unknown, editIndex?: number | null): string[] => {
  const keyword = normalizeSentimentKeywordText(value);
  if (!keyword) return normalizeSentimentKeywordList(keywords);
  const nextKeywords = [...keywords];
  if (editIndex !== undefined && editIndex !== null && editIndex >= 0 && editIndex < nextKeywords.length) {
    nextKeywords[editIndex] = keyword;
  } else {
    nextKeywords.push(keyword);
  }
  return normalizeSentimentKeywordList(nextKeywords);
};

export const removeSentimentKeyword = (keywords: string[], index: number): string[] =>
  normalizeSentimentKeywordList(keywords.filter((_keyword, currentIndex) => currentIndex !== index));

export const mergeSentimentKeywordLists = (base: unknown, additions: unknown): string[] =>
  normalizeSentimentKeywordList([...normalizeSentimentKeywordList(base), ...normalizeSentimentKeywordList(additions)]);
